import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { categories } from '@/mocks/categories';

const milestones = [
  { year: '起步', title: '聚合多类信息', desc: '从教育、娱乐、购物三个高频领域出发，搭建统一的信息入口。' },
  { year: '成长', title: '完善分类体系', desc: '扩展至生活、健康、职场、科技、旅游等八大类目，层级更清晰。' },
  { year: '升级', title: '强化查找体验', desc: '引入多维筛选、智能排序与关键词模糊匹配，让查找更高效。' },
  { year: '愿景', title: '一站式服务平台', desc: '持续丰富内容与服务形态，成为用户放心的在线资源查询入口。' },
];

const values = [
  { icon: 'ri-compass-3-line', title: '信息清晰', desc: '分类层级分明，模块区块化呈现，让信息一目了然。' },
  { icon: 'ri-speed-up-line', title: '高效便捷', desc: '搜索、筛选、排序三步到位，减少无效浏览时间。' },
  { icon: 'ri-shield-star-line', title: '真实可靠', desc: '内容来自多方整理，标注来源与更新时间，便于判断。' },
  { icon: 'ri-user-heart-line', title: '用户至上', desc: '以使用者视角设计每一个入口与交互，持续优化体验。' },
];

export default function About() {
  useSeo({
    title: '关于我们 - 在线服务平台',
    description: '了解在线多元化综合信息服务平台的定位、发展目标、团队理念与版权声明，专注为你提供便捷的在线资源查询体验。',
    keywords: '关于在线,在线平台介绍,在线服务平台,平台理念',
  });

  return (
    <div className="w-full">
      <section className="w-full px-4 md:px-8 py-10 md:py-16 bg-primary-950">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 border border-background-50/20 text-background-50 text-xs md:text-sm mb-5">
            <i className="ri-information-line text-accent-400"></i>关于在线
          </span>
          <h1 className="text-2xl md:text-4xl font-bold text-background-50 leading-tight">
            专注在线信息聚合，<br className="hidden md:block" />让资源查找更高效
          </h1>
          <p className="mt-5 text-sm md:text-base text-primary-100 leading-relaxed">
            在线是一个多元化综合信息服务平台，围绕「信息聚合 + 分类浏览 + 精准筛选 + 内容详情」构建核心体验，帮助用户在一站式的环境中快速定位所需资源。我们相信，清晰的信息结构本身就是一种效率。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">平台定位与目标</h2>
            <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              <p>
                在线的目标用户是寻求一站式在线资源与服务体验的广泛网民，尤其适合追求高效、便捷与信息多样性的使用者。无论你是想找一门技能课程、一场周末活动、一次购物优惠，还是需要生活服务与出行攻略，都可以在这里按分类快速找到。
              </p>
              <p>
                平台目前覆盖八大服务类目，包括 {categories.map((c) => c.name).join('、')}。每个类目下细分多个子分类，并支持按地区、服务类型与更新时间进行组合筛选，配合最新、最热、推荐三种排序方式，帮助不同需求的用户快速缩小范围。
              </p>
              <p>
                我们不做复杂的业务闭环，而是专注把信息梳理清楚。所有内容均经过结构化整理，标注提供方与发布时间，让你在浏览时有据可依。
              </p>
            </div>
          </div>

          <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6 h-fit">
            <h2 className="text-base font-semibold text-foreground-900 mb-4 flex items-center gap-2">
              <i className="ri-road-map-line text-primary-600"></i>发展历程
            </h2>
            <ol className="relative space-y-5">
              {milestones.map((m) => (
                <li key={m.year} className="pl-5 border-l-2 border-primary-100">
                  <span className="inline-block text-xs px-2 py-0.5 rounded-md bg-accent-100 text-accent-700 mb-1.5">{m.year}</span>
                  <h3 className="text-sm font-semibold text-foreground-900">{m.title}</h3>
                  <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{m.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-16 bg-background-100">
        <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">我们的核心理念</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {values.map((v) => (
            <div key={v.title} className="p-5 rounded-lg bg-background-50 border border-background-200">
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 text-2xl mb-3">
                <i className={v.icon}></i>
              </span>
              <h3 className="text-base font-semibold text-foreground-900">{v.title}</h3>
              <p className="text-sm text-foreground-500 mt-2 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          <div className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-7">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-team-line text-primary-600"></i>团队简介
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              在线由一支专注信息产品与内容运营的小团队维护，成员来自产品设计、内容编辑与技术开发等方向。我们既关注信息本身的准确性与可读性，也在意每一个页面的浏览体验，力求让查找资源这件事变得更轻松。
            </p>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
              团队持续收集用户反馈，不断优化分类结构、筛选维度与内容呈现方式，让平台随着使用需求一起成长。
            </p>
          </div>

          <div className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-7">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-copyright-line text-primary-600"></i>版权及法律声明
            </h2>
            <ul className="space-y-3 text-sm text-foreground-600 leading-relaxed">
              <li className="flex gap-2"><i className="ri-checkbox-blank-circle-fill text-[6px] text-accent-500 mt-2"></i><span>本平台内容为信息聚合与演示用途，具体服务详情请以实际提供方公布信息为准。</span></li>
              <li className="flex gap-2"><i className="ri-checkbox-blank-circle-fill text-[6px] text-accent-500 mt-2"></i><span>平台标识、页面设计与原创文字内容受相关法律保护，未经许可请勿用于商业用途。</span></li>
              <li className="flex gap-2"><i className="ri-checkbox-blank-circle-fill text-[6px] text-accent-500 mt-2"></i><span>若内容涉及权利问题，欢迎通过联系页面反馈，我们会及时核实处理。</span></li>
              <li className="flex gap-2"><i className="ri-checkbox-blank-circle-fill text-[6px] text-accent-500 mt-2"></i><span>© 2026 在线 · 多元化在线服务平台 · 保留所有权利。</span></li>
            </ul>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/categories" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-apps-2-line"></i>浏览全部分类
          </Link>
          <Link to="/contact" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm rounded-md border border-background-300 text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-chat-smile-2-line"></i>联系反馈
          </Link>
        </div>
      </section>
    </div>
  );
}